import type PocketBase from 'pocketbase';
import type { NavigateFunction } from 'react-router-dom';
import type { useSignup } from './use-registration-api';
import type { FieldErrors, SignupFields } from './use-form-state';
import {
  translateBlockReason,
  translateErrorMap,
  validateClientSide,
} from './signup-errors';

type Deps = {
  data: SignupFields;
  agreed: boolean;
  signup: ReturnType<typeof useSignup>;
  getSessionToken: () => string | null;
  clearSession: () => void;
  setError: (msg: string) => void;
  setFieldErrors: (f: FieldErrors) => void;
  setAgreedError: (m: string) => void;
  navigate: NavigateFunction;
  pb: PocketBase;
  refreshAuth: () => Promise<void>;
};

function buildPayload(
  d: SignupFields,
  token: string
): Parameters<ReturnType<typeof useSignup>['mutateAsync']>[0] {
  const phone = `${d.phone_prefix}${d.phone_number.replace(/\s/g, '')}`;
  return { ...d, phone, session_token: token };
}

function errorBody(err: unknown): Record<string, unknown> | undefined {
  const withResp = err as { response?: Record<string, unknown> };
  const withData = err as { data?: Record<string, unknown> };
  return withResp?.response ?? withData?.data;
}

type Extracted = { fieldErrors?: FieldErrors; message?: string };
function extractErrors(err: unknown): Extracted {
  const body = errorBody(err);
  if (!body) return { message: (err as Error)?.message ?? 'Error' };
  const errors = body['errors'];
  if (errors && typeof errors === 'object') {
    return { fieldErrors: translateErrorMap(errors as FieldErrors) };
  }
  const msg = body['error'] ?? body['reason'];
  return { message: typeof msg === 'string' ? msg : 'Error' };
}

function runClientSide(d: Deps): boolean {
  const fe = validateClientSide(d.data);
  const badA = !d.agreed;
  d.setAgreedError(badA ? 'Debes aceptar los términos y la política' : '');
  d.setFieldErrors(fe);
  return Object.keys(fe).length > 0 || badA;
}

async function finishAuth(
  d: Deps,
  token: string,
  member: unknown
): Promise<void> {
  d.pb.authStore.save(
    token,
    member as Parameters<typeof d.pb.authStore.save>[1]
  );
  d.clearSession();
  await d.refreshAuth();
}

function applyError(d: Deps, err: unknown): void {
  const { fieldErrors, message } = extractErrors(err);
  if (fieldErrors) d.setFieldErrors(fieldErrors);
  else if (message) d.setError(message);
}

async function submit(d: Deps): Promise<void> {
  d.setError('');
  if (runClientSide(d)) return;

  const token = d.getSessionToken();
  if (!token) {
    d.setError('Sesión expirada. Vuelve a empezar.');
    return;
  }

  try {
    const res = await d.signup.mutateAsync(buildPayload(d.data, token));
    if (res.blocked) {
      d.setFieldErrors({});
      d.setError(translateBlockReason(res.reason));
      return;
    }
    if (res.token) await finishAuth(d, res.token, res.member);
    d.navigate('/');
  } catch (err) {
    applyError(d, err);
  }
}

export function useSignupSubmit(deps: Deps): {
  handleSubmit: () => Promise<void>;
} {
  return { handleSubmit: () => submit(deps) };
}
